import type {UserMediaType, UploadFileDataTypes, UserMediaVOResponse} from '@Libraries/user-media-library';
import {uploadFiles} from '@Libraries/user-media-library';
import {isTempImageData} from '@Libraries/add-media-library';
import type {PromptAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {convertUserMediaVOToAttachment, LoadingAttachmentsTracker, MAX_ATTACHMENTS, validatePresetsInBackground} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import isArray from 'lodash/isArray';

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export const AI_PROMPT_BOX_ACCEPTED_IMAGE_TYPES = ACCEPTED_IMAGE_TYPES;

export interface DragDropUploadState {
  getAttachmentCount: () => number;
  addLoadingAttachments: (count: number) => string[];
  removeLoadingAttachments: (ids: string[]) => void;
  addAttachments: (attachments: PromptAttachment[]) => void;
  removeImageAttachments: (hashedFilenames: string[]) => void;
  setActive: () => void;
}

export const handleDragDropFilesUpload = (files: File[], stateAccessors: DragDropUploadState, onInvalidFileTypeError?: VoidFunction): void => {
  const currentAttachmentCount = stateAccessors.getAttachmentCount();
  if (currentAttachmentCount >= MAX_ATTACHMENTS) {
    return;
  }

  const imageFiles = files.filter((file) => ACCEPTED_IMAGE_TYPES.includes(file.type));
  if (imageFiles.length === 0) {
    if (files.length > 0) {
      onInvalidFileTypeError?.();
    }
    return;
  }

  const availableSlots = MAX_ATTACHMENTS - currentAttachmentCount;
  const filesToUpload = imageFiles.slice(0, availableSlots);

  const tracker = new LoadingAttachmentsTracker();
  const loadingIds = stateAccessors.addLoadingAttachments(filesToUpload.length);
  tracker.addIds(loadingIds);

  stateAccessors.setActive();

  void uploadFiles(
    {
      onFileUpload: (_type: UserMediaType, data: UploadFileDataTypes): void => {
        const responses = extractNonTempResponses(data);
        if (responses.length > 0) {
          promoteUploadedAttachments(responses, tracker, stateAccessors);
        }
      },
      onUploadError: (): void => {
        const {idsToRemove} = tracker.resolveIds(1);
        stateAccessors.removeLoadingAttachments(idsToRemove);
      },
    },
    filesToUpload,
    {shouldLoadDataUrlImage: false}
  );
};

const extractNonTempResponses = (data: UploadFileDataTypes): UserMediaVOResponse[] => {
  if (isArray(data)) {
    return data.filter((item): item is UserMediaVOResponse => !isTempImageData(item));
  }

  if (!isTempImageData(data)) {
    return [data];
  }

  return [];
};

const promoteUploadedAttachments = (responses: UserMediaVOResponse[], tracker: LoadingAttachmentsTracker, stateAccessors: DragDropUploadState): void => {
  const attachments = responses.map((response) => convertUserMediaVOToAttachment(response, PromptAttachmentType.IMAGE));
  const {idsToRemove, activeIds} = tracker.resolveIds(responses.length);
  stateAccessors.removeLoadingAttachments(idsToRemove);

  if (activeIds.length === 0) {
    return;
  }

  const activeAttachments = attachments.slice(0, activeIds.length);
  stateAccessors.addAttachments(activeAttachments);

  void validatePresetsInBackground(
    activeAttachments.map((attachment) => attachment.hashedFilename),
    stateAccessors.removeImageAttachments
  );
};
